import React, { useMemo, useState } from "react";
import { Container, Card, Button, Form, Row, Col, ProgressBar, Table, ListGroup } from "react-bootstrap";
import JSZip from "jszip";

// ------------------------------------------------------------
// Pliable Synthetic Data Generator (Front-end only)
// Mirrors the Python logic you approved:
// - Retail brand: Amazon, Shopify, Target+, Walmart, Wholesale (NetSuite)
// - Ads: Meta, Google, AmazonAds, TikTok, Mountain
// - Analytics: GA4, Klaviyo, Stampd (reviews)
// - 3 years daily through Q4 2025
// - Separate order + item datasets per channel, plus ads, GA4, Klaviyo, reviews
// ------------------------------------------------------------

// Small deterministic RNG so data is repeatable per seed
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return function () {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rng: () => number, min: number, max: number) {
  return Math.floor(rng() * (max - min + 1)) + min;
}
function randFloat(rng: () => number, min: number, max: number) {
  return rng() * (max - min) + min;
}
function choice<T>(rng: () => number, arr: T[], weights?: number[]) {
  if (!weights) return arr[randInt(rng, 0, arr.length - 1)];
  const total = weights.reduce((a, b) => a + b, 0);
  let r = rng() * total;
  for (let i = 0; i < arr.length; i++) {
    r -= weights[i];
    if (r <= 0) return arr[i];
  }
  return arr[arr.length - 1];
}

function dateRange(start: Date, end: Date) {
  const res: Date[] = [];
  const d = new Date(start);
  while (d <= end) {
    res.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return res;
}

function formatDateISO(d: Date) {
  return d.toISOString().slice(0, 10);
}

function toCSV(rows: Record<string, any>[]) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const esc = (v: any) => {
    if (v == null) return "";
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines = [headers.join(",")];
  for (const r of rows) {
    lines.push(headers.map((h) => esc(r[h])).join(","));
  }
  return lines.join("\n");
}

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// Opens CSV in a new tab for manual copy/save (works even if downloads are blocked or blob: URLs are restricted)
function openCSVInTab(filename: string, text: string) {
  // Use a data: URL instead of blob: — this works in more embedded/sandboxed environments
  const url = "data:text/csv;charset=utf-8," + encodeURIComponent(text);
  const win = window.open(url, "_blank");
  if (win) {
    win.onload = () => {
      win.document.title = filename;
    };
  }
}

// Some browsers block automatic downloads even on user click.
// This tries the File System Access API first (Chrome/Edge), then falls back.
async function saveCSV(filename: string, text: string) {
  try {
    if (typeof window !== "undefined" && (window as any).showSaveFilePicker) {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: "CSV file",
            accept: { "text/csv": [".csv"] },
          },
        ],
      });
      const writable = await handle.createWritable();
      await writable.write(text);
      await writable.close();
      return;
    }
  } catch (e) {
    // user cancelled or unsupported — fallback to auto download
  }

  // Fallback: in stricter environments, even this may be blocked, so we also support opening in a new tab
  openCSVInTab(filename, text);
}

async function downloadZip(filename: string, files: Record<string, string>) {
  const zip = new JSZip();
  for (const [name, content] of Object.entries(files)) {
    zip.file(name, content);
  }
  const blob = await zip.generateAsync({ type: "blob" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

// ------------------------------------------------------------
// Core generation logic (JS)
// ------------------------------------------------------------
function generateRetailDataset({
  seed = 42,
  startDate = "2023-01-01",
  endDate = "2025-12-31",
  numSkus = 300,
  heroCategories = 3,
  includeAttributionMismatch = false,
  scale = 1.0,
  onProgress,
}: {
  seed?: number;
  startDate?: string;
  endDate?: string;
  numSkus?: number;
  heroCategories?: number;
  includeAttributionMismatch?: boolean;
  scale?: number;
  onProgress?: (pct: number) => void;
}) {
  const rng = mulberry32(seed);

  const start = new Date(startDate);
  const end = new Date(endDate);
  const dates = dateRange(start, end);
  const totalDays = dates.length - 1;

  const channels = ["Shopify", "Amazon", "TargetPlus", "Walmart", "Wholesale"] as const;
  const channelSystem: Record<(typeof channels)[number], string> = {
    Shopify: "Shopify",
    Amazon: "AmazonSellerCentral",
    TargetPlus: "TargetPlusPortal",
    Walmart: "WalmartSellerCenter",
    Wholesale: "NetSuite",
  };
  const channelBaseOrders: Record<(typeof channels)[number], number> = {
    Shopify: 70,
    Amazon: 110,
    TargetPlus: 15,
    Walmart: 20,
    Wholesale: 10,
  };
  const baseFeeRates: Record<(typeof channels)[number], number> = {
    Shopify: 0.03,
    Amazon: 0.15,
    TargetPlus: 0.12,
    Walmart: 0.15,
    Wholesale: 0.0,
  };
  const baseDiscountRates: Record<(typeof channels)[number], number> = {
    Shopify: 0.1,
    Amazon: 0.06,
    TargetPlus: 0.08,
    Walmart: 0.08,
    Wholesale: 0.03,
  };

  const heroCats = Array.from({ length: heroCategories }, (_, i) => `Hero_${String.fromCharCode(65 + i)}`);
  const otherCats = ["Category_1", "Category_2", "Category_3", "Category_4", "Category_5"];
  const categories = [...heroCats, ...otherCats];

  // Category probabilities: ~60% hero, rest spread
  const heroProb = 0.6 / heroCats.length;
  const otherProb = 0.4 / otherCats.length;
  const catProbs = categories.map((c) => (heroCats.includes(c) ? heroProb : otherProb));

  const productMaster: any[] = [];
  for (let i = 1; i <= numSkus; i++) {
    const sku = `SKU${String(i).padStart(4, "0")}`;
    const cat = choice(rng, categories, catProbs);
    const baseCost = Math.round(randFloat(rng, 5, 50) * 100) / 100;
    const listPrice = Math.round(baseCost * randFloat(rng, 1.7, 3.0) * 100) / 100;
    productMaster.push({
      SKU: sku,
      ProductName: `Product ${i}`,
      Category: cat,
      BaseCost: baseCost,
      ListPrice: listPrice,
    });
  }

  const heroSkus = productMaster.filter((p) => heroCats.includes(p.Category)).map((p) => p.SKU);
  const otherSkus = productMaster.filter((p) => !heroCats.includes(p.Category)).map((p) => p.SKU);
  const productBySku = new Map(productMaster.map((p) => [p.SKU, p]));

  // Helper: time-based factors
  function growthFactor(d: Date) {
    const t = (d.getTime() - start.getTime()) / (end.getTime() - start.getTime());
    return 1.0 + 0.5 * t;
  }
  function seasonalMultiplier(d: Date) {
    const monthFactors: Record<number, number> = {
      1: 0.8, 2: 0.85, 3: 0.95, 4: 0.9, 5: 1.0, 6: 1.05,
      7: 1.1, 8: 1.2, 9: 1.0, 10: 1.25, 11: 1.6, 12: 1.9,
    };
    let f = monthFactors[d.getMonth() + 1] ?? 1.0;
    const wd = d.getDay();
    if (wd === 0 || wd === 6) f *= 1.1;
    const m = d.getMonth() + 1;
    const day = d.getDate();
    if ((m === 3 || m === 8) && Math.abs(day - 15) <= 2) f *= 1.5;
    return f;
  }
  function cogsInflation(d: Date) {
    const t = (d.getTime() - start.getTime()) / (end.getTime() - start.getTime());
    return 1.0 + 0.2 * t;
  }
  function feeRate(channel: (typeof channels)[number], d: Date) {
    const t = (d.getTime() - start.getTime()) / (end.getTime() - start.getTime());
    return baseFeeRates[channel] * (1.0 + 0.3 * t);
  }
  function discountRate(channel: (typeof channels)[number], d: Date) {
    let r = baseDiscountRates[channel];
    const m = d.getMonth() + 1;
    const day = d.getDate();
    if ([10, 11, 12].includes(m)) r += 0.05;
    if ((m === 3 || m === 8) && Math.abs(day - 15) <= 2) r += 0.04;
    return Math.min(r, 0.3);
  }
  function merTarget(channel: (typeof channels)[number], d: Date) {
    const t = (d.getTime() - start.getTime()) / (end.getTime() - start.getTime());
    let base, endv;
    if (channel === "Shopify") { base = 3.5; endv = 2.3; }
    else if (channel === "Amazon") { base = 5.0; endv = 3.5; }
    else { base = 4.0; endv = 2.5; }
    return Math.max(endv, base + (endv - base) * t);
  }

  const ordersByChannel: Record<string, any[]> = {};
  const itemsByChannel: Record<string, any[]> = {};

  // Simple Poisson sampler
  function poisson(lam: number) {
    const L = Math.exp(-lam);
    let k = 0;
    let p = 1.0;
    do { k++; p *= rng(); } while (p > L);
    return k - 1;
  }

  let processedDays = 0;
  for (const ch of channels) {
    const orders: any[] = [];
    const items: any[] = [];
    let orderCounter = 1;

    for (const d of dates) {
      const lam = Math.max(channelBaseOrders[ch] * seasonalMultiplier(d) * growthFactor(d) * scale, 0.1);
      const nOrders = poisson(lam);
      if (nOrders === 0) { processedDays++; continue; }

      for (let i = 0; i < nOrders; i++) {
        const orderId = `${ch.slice(0, 3).toUpperCase()}_${String(orderCounter).padStart(7, "0")}`;
        orderCounter++;

        const t = (d.getTime() - start.getTime()) / (end.getTime() - start.getTime());
        const pNew = Math.max(0.15, 0.4 - 0.15 * t);
        const customerType = rng() < pNew ? "New" : "Returning";

        orders.push({
          OrderID: orderId,
          OrderDate: formatDateISO(d),
          Channel: ch,
          SourceSystem: channelSystem[ch],
          CustomerType: customerType,
        });

        const numLines = randInt(rng, 1, 5);
        for (let ln = 1; ln <= numLines; ln++) {
          const sku = rng() < 0.7 ? choice(rng, heroSkus) : choice(rng, otherSkus);
          const prod = productBySku.get(sku);
          const qty = randInt(rng, 1, 4);

          let price = prod.ListPrice;
          if (ch === "Wholesale") price *= 0.6;
          else if (["Amazon", "Walmart", "TargetPlus"].includes(ch)) price *= randFloat(rng, 0.9, 1.05);
          else price *= randFloat(rng, 0.95, 1.1);

          price = Math.round(price * 100) / 100;
          const gross = Math.round(price * qty * 100) / 100;
          const cogs = Math.round(prod.BaseCost * qty * cogsInflation(d) * 100) / 100;

          items.push({
            OrderID: orderId,
            LineNumber: ln,
            SKU: sku,
            Category: prod.Category,
            Quantity: qty,
            UnitPrice: price,
            GrossRevenue: gross,
            BaseCost: prod.BaseCost,
            COGSLine: cogs,
          });
        }
      }

      processedDays++;
      if (processedDays % 30 === 0 && onProgress) {
        onProgress(Math.round((processedDays / (dates.length * channels.length)) * 100));
      }
    }

    // Aggregate to order level
    const itemsByOrder = new Map<string, { GrossRevenue: number; Units: number; COGS: number }>();
    for (const it of items) {
      const agg = itemsByOrder.get(it.OrderID) || { GrossRevenue: 0, Units: 0, COGS: 0 };
      agg.GrossRevenue += it.GrossRevenue;
      agg.Units += it.Quantity;
      agg.COGS += it.COGSLine;
      itemsByOrder.set(it.OrderID, agg);
    }

    for (const o of orders) {
      const agg = itemsByOrder.get(o.OrderID) || { GrossRevenue: 0, Units: 0, COGS: 0 };
      const od = new Date(o.OrderDate);
      const disc = Math.round(agg.GrossRevenue * discountRate(ch, od) * 100) / 100;
      const net = Math.round((agg.GrossRevenue - disc) * 100) / 100;
      const fee = Math.round(agg.GrossRevenue * feeRate(ch, od) * 100) / 100;

      o.GrossRevenue = Math.round(agg.GrossRevenue * 100) / 100;
      o.Units = agg.Units;
      o.COGS = Math.round(agg.COGS * 100) / 100;
      o.Discounts = disc;
      o.NetRevenue = net;
      o.ChannelFee = fee;
    }

    ordersByChannel[ch] = orders;
    itemsByChannel[ch] = items;
  }

  // Daily summary
  const ordersAll = Object.values(ordersByChannel).flat();
  const dailyKey = (d: string, ch: string) => `${d}|${ch}`;
  const dailyMap = new Map<string, any>();

  for (const o of ordersAll) {
    const key = dailyKey(o.OrderDate, o.Channel);
    const cur = dailyMap.get(key) || {
      OrderDate: o.OrderDate,
      Channel: o.Channel,
      Orders: 0,
      Units: 0,
      GrossRevenue: 0,
      Discounts: 0,
      NetRevenue: 0,
      COGS: 0,
      ChannelFee: 0,
    };
    cur.Orders += 1;
    cur.Units += o.Units;
    cur.GrossRevenue += o.GrossRevenue;
    cur.Discounts += o.Discounts;
    cur.NetRevenue += o.NetRevenue;
    cur.COGS += o.COGS;
    cur.ChannelFee += o.ChannelFee;
    dailyMap.set(key, cur);
  }

  const ordersDailySummary = Array.from(dailyMap.values()).map((r) => ({
    ...r,
    GrossRevenue: Math.round(r.GrossRevenue * 100) / 100,
    Discounts: Math.round(r.Discounts * 100) / 100,
    NetRevenue: Math.round(r.NetRevenue * 100) / 100,
    COGS: Math.round(r.COGS * 100) / 100,
    ChannelFee: Math.round(r.ChannelFee * 100) / 100,
  }));

  // Ad performance
  const adPlatforms: Record<string, string[]> = {
    Meta: ["Meta_Prospecting", "Meta_Retargeting", "Meta_HeroLaunch"],
    Google: ["Google_Brand", "Google_NonBrand", "Google_Shopping"],
    AmazonAds: ["Amazon_SponsoredProducts", "Amazon_Brand", "Amazon_Deals"],
    TikTok: ["TikTok_TOF", "TikTok_Retargeting"],
    Mountain: ["Mountain_CTV_Prospecting", "Mountain_CTV_Retention"],
  };
  const platformWeightsByChannel: Record<string, Record<string, number>> = {
    Shopify: { Meta: 0.45, Google: 0.35, TikTok: 0.15, Mountain: 0.05 },
    Amazon: { AmazonAds: 1.0 },
  };
  const platformParams: Record<string, { cpm: number; ctr: number }> = {
    Meta: { cpm: 12.0, ctr: 0.011 },
    Google: { cpm: 10.0, ctr: 0.032 },
    AmazonAds: { cpm: 8.0, ctr: 0.02 },
    TikTok: { cpm: 6.0, ctr: 0.015 },
    Mountain: { cpm: 18.0, ctr: 0.004 },
  };

  const adPerformance: any[] = [];
  for (const r of ordersDailySummary) {
    const dateStr = r.OrderDate;
    const ch = r.Channel;
    const rev = r.NetRevenue;
    if (rev <= 0) continue;
    if (!platformWeightsByChannel[ch]) continue;

    const d = new Date(dateStr);
    const mer = merTarget(ch, d);
    const totalSpend = rev / mer;

    const weights = platformWeightsByChannel[ch];
    for (const platform of Object.keys(weights)) {
      const platformSpend = totalSpend * weights[platform];
      if (platformSpend <= 0) continue;

      const campaigns = adPlatforms[platform];
      const rawShares = campaigns.map(() => rng());
      const sumShares = rawShares.reduce((a, b) => a + b, 0);
      const shares = rawShares.map((s) => s / sumShares);

      const params = platformParams[platform];
      const cpm = params.cpm;
      const ctr = params.ctr;

      campaigns.forEach((campaign, idx) => {
        const spend = platformSpend * shares[idx];
        if (spend < 5) return;
        const impressions = (spend * 1000.0) / cpm;
        const clicks = Math.max(1, Math.floor(impressions * ctr));
        const cpc = spend / clicks;
        const roas = randFloat(rng, 1.5, 4.0);
        const attrRev = spend * roas;

        adPerformance.push({
          Date: dateStr,
          Channel: ch,
          Platform: platform,
          Campaign: campaign,
          Spend: Math.round(spend * 100) / 100,
          Impressions: Math.floor(impressions),
          Clicks: clicks,
          CPC: Math.round(cpc * 100) / 100,
          PlatformAttributedRevenue: Math.round(attrRev * 100) / 100,
          Category: choice(rng, heroCats.concat(otherCats)),
        });
      });
    }
  }

  // GA4 daily
  const convRates: Record<string, number> = {
    Shopify: 0.03,
    Amazon: 0.12,
    TargetPlus: 0.08,
    Walmart: 0.07,
    Wholesale: 0.15,
  };
  const ga4Daily = ordersDailySummary.map((r) => {
    const d = new Date(r.OrderDate);
    const ch = r.Channel;
    const orders = r.Orders;
    const revenue = r.NetRevenue;
    const conv = convRates[ch] || 0.05;
    const sessions = Math.max(orders, Math.floor(orders / conv));
    const users = Math.floor(sessions * randFloat(rng, 0.7, 0.95));
    const t = (d.getTime() - start.getTime()) / (end.getTime() - start.getTime());
    const newUserRatio = Math.max(0.2, 0.4 - 0.15 * t);
    const newUsers = Math.floor(users * newUserRatio);
    return {
      Date: r.OrderDate,
      Channel: ch,
      Sessions: sessions,
      Users: users,
      NewUsers: newUsers,
      Transactions: orders,
      Revenue: Math.round(revenue * 100) / 100,
    };
  });

  // Klaviyo daily (Shopify only)
  const klaviyoDaily: any[] = [];
  for (const r of ordersDailySummary) {
    if (r.Channel !== "Shopify") continue;
    const netRev = r.NetRevenue;
    if (netRev <= 0) continue;
    const share = randFloat(rng, 0.15, 0.35);
    const emailRev = netRev * share;
    const sends = Math.max(50, Math.floor(emailRev / randFloat(rng, 1.5, 4.0)));
    const openRate = randFloat(rng, 0.25, 0.45);
    const clickRate = randFloat(rng, 0.02, 0.08);
    const convRate = randFloat(rng, 0.02, 0.06);
    const opens = Math.floor(sends * openRate);
    const clicks = Math.floor(sends * clickRate);
    const placedOrders = Math.max(1, Math.floor(clicks * convRate));
    klaviyoDaily.push({
      Date: r.OrderDate,
      Channel: "Shopify",
      EmailsSent: sends,
      Opens: opens,
      Clicks: clicks,
      PlacedOrders: placedOrders,
      Revenue: Math.round(emailRev * 100) / 100,
    });
  }

  // Stampd reviews
  const stampdReviews: any[] = [];
  for (const p of productMaster) {
    const lam = heroCats.includes(p.Category) ? 30 : 5;
    const numReviews = poisson(lam);
    for (let i = 0; i < numReviews; i++) {
      const randIndex = randInt(rng, 0, totalDays);
      const rd = dates[randIndex];
      const rating = Math.round(randFloat(rng, 3.0, 5.0) * 10) / 10;
      stampdReviews.push({
        ReviewID: `R_${p.SKU}_${String(randInt(rng, 1, 999999)).padStart(6, "0")}`,
        SKU: p.SKU,
        Category: p.Category,
        ReviewDate: formatDateISO(rd),
        Rating: rating,
        Source: choice(rng, ["Amazon", "Shopify", "Walmart", "TargetPlus"]),
      });
    }
  }

  if (onProgress) onProgress(100);

  return {
    Product_Master: productMaster,
    Orders_By_Channel: ordersByChannel,
    Order_Items_By_Channel: itemsByChannel,
    Orders_Daily_Summary: ordersDailySummary,
    Ad_Performance: adPerformance,
    GA4_Daily_Channel: ga4Daily,
    Klaviyo_Daily: klaviyoDaily,
    Stampd_Reviews: stampdReviews,
  };
}

export default function SyntheticDataGeneratorApp() {
  const [seed, setSeed] = useState(42);
  const [startDate, setStartDate] = useState("2023-01-01");
  const [endDate, setEndDate] = useState("2025-12-31");
  const [numSkus, setNumSkus] = useState(300);
  const [heroCategories, setHeroCategories] = useState(3);
  const [scale, setScale] = useState(1.0);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [data, setData] = useState<any | null>(null);

  const summary = useMemo(() => {
    if (!data) return null;
    const ordersAll = Object.values(data.Orders_By_Channel).flat();
    const adRows = data.Ad_Performance.length;
    return {
      products: data.Product_Master.length,
      orders: ordersAll.length,
      adRows,
      ga4Rows: data.GA4_Daily_Channel.length,
      klaviyoRows: data.Klaviyo_Daily.length,
      reviewRows: data.Stampd_Reviews.length,
    };
  }, [data]);

  const handleGenerate = () => {
    setGenerating(true);
    setProgress(0);

    setTimeout(() => {
      const ds = generateRetailDataset({
        seed: Number(seed) || 42,
        startDate,
        endDate,
        numSkus: Number(numSkus) || 300,
        heroCategories: Number(heroCategories) || 3,
        scale: Number(scale) || 1.0,
        onProgress: setProgress,
      });
      setData(ds);
      setGenerating(false);
    }, 50);
  };

  const handleResetSeed = () => {
    setSeed(randInt(Math.random, 1, 10_000));
    setData(null);
  };

  const buildFilesMap = () => {
    if (!data) return {} as Record<string, string>;
    const files: Record<string, string> = {};
    files["Product_Master.csv"] = toCSV(data.Product_Master);

    for (const [ch, rows] of Object.entries(data.Orders_By_Channel)) {
      files[`Orders_${ch}.csv`] = toCSV(rows as any[]);
    }
    for (const [ch, rows] of Object.entries(data.Order_Items_By_Channel)) {
      files[`Order_Items_${ch}.csv`] = toCSV(rows as any[]);
    }

    files["Orders_Daily_Summary.csv"] = toCSV(data.Orders_Daily_Summary);
    files["Ad_Performance.csv"] = toCSV(data.Ad_Performance);
    files["GA4_Daily_Channel.csv"] = toCSV(data.GA4_Daily_Channel);
    files["Klaviyo_Daily.csv"] = toCSV(data.Klaviyo_Daily);
    files["Stampd_Reviews.csv"] = toCSV(data.Stampd_Reviews);
    return files;
  };

  const downloadAll = async () => {
    if (!data) return;
    const files = buildFilesMap();
    await downloadZip("pliable_retail_q4_dataset.zip", files);
  };

  return (
    <Container className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h2 mb-0">Pliable Synthetic Data Generator</h1>
        <Button variant="outline-secondary" onClick={handleResetSeed}>
           New Seed
        </Button>
      </div>

      <Card className="mb-4">
        <Card.Header>
          <Card.Title>Retail Q4 Advertising Trends Dataset</Card.Title>
          <p className="text-muted mb-0 small">
            Generates 3 years of daily retail + ads + analytics data for a brand selling on Amazon, Shopify, Target+, Walmart, and Wholesale (NetSuite).
          </p>
        </Card.Header>
        <Card.Body>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Seed (repeatable)</Form.Label>
                <Form.Control 
                  type="number" 
                  value={seed} 
                  onChange={(e) => setSeed(Number(e.target.value))} 
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Scale (volume multiplier)</Form.Label>
                <Form.Control 
                  type="number" 
                  step="0.1" 
                  min="0.1" 
                  value={scale} 
                  onChange={(e) => setScale(Number(e.target.value))} 
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Start date</Form.Label>
                <Form.Control 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)} 
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>End date</Form.Label>
                <Form.Control 
                  type="date" 
                  value={endDate} 
                  onChange={(e) => setEndDate(e.target.value)} 
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label># SKUs</Form.Label>
                <Form.Control 
                  type="number" 
                  min="50" 
                  value={numSkus} 
                  onChange={(e) => setNumSkus(Number(e.target.value))} 
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label># Hero categories</Form.Label>
                <Form.Select 
                  value={String(heroCategories)} 
                  onChange={(e) => setHeroCategories(Number(e.target.value))}
                >
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} className="d-flex gap-2 pt-2">
              <Button onClick={handleGenerate} disabled={generating} variant="primary">
                 {generating ? "Generating…" : "Generate dataset"}
              </Button>
              {data && (
                <Button variant="secondary" onClick={downloadAll}>
                   Download all CSVs (ZIP)
                </Button>
              )}
            </Col>

            {generating && (
              <Col xs={12} className="pt-2">
                <ProgressBar now={progress} label={`${progress}%`} />
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>

      {summary && (
        <Card className="mb-4">
          <Card.Header>
            <Card.Title>Generated tables</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={4}>
                <div className="p-3 bg-light rounded">
                  Product_Master: <strong>{summary.products.toLocaleString()}</strong>
                </div>
              </Col>
              <Col md={4}>
                <div className="p-3 bg-light rounded">
                  Orders (all): <strong>{summary.orders.toLocaleString()}</strong>
                </div>
              </Col>
              <Col md={4}>
                <div className="p-3 bg-light rounded">
                  Ad_Performance: <strong>{summary.adRows.toLocaleString()}</strong>
                </div>
              </Col>
              <Col md={4}>
                <div className="p-3 bg-light rounded">
                  GA4_Daily_Channel: <strong>{summary.ga4Rows.toLocaleString()}</strong>
                </div>
              </Col>
              <Col md={4}>
                <div className="p-3 bg-light rounded">
                  Klaviyo_Daily: <strong>{summary.klaviyoRows.toLocaleString()}</strong>
                </div>
              </Col>
              <Col md={4}>
                <div className="p-3 bg-light rounded">
                  Stampd_Reviews: <strong>{summary.reviewRows.toLocaleString()}</strong>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {data && (
        <Card className="mb-4">
          <Card.Header>
            <Card.Title>Download individual files</Card.Title>
            <p className="text-muted mb-0 small">
              If your browser blocks downloads, use "Open in new tab" and save manually.
            </p>
          </Card.Header>
          <Card.Body>
            <ListGroup>
              {Object.entries(buildFilesMap()).map(([name, content]) => (
                <ListGroup.Item key={name} className="d-flex justify-content-between align-items-center">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={async () => saveCSV(name, content)}
                  >
                     {name}
                  </Button>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => openCSVInTab(name, content)}
                  >
                     Open in new tab
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
            <p className="text-muted mt-3 mb-0 small">
              Tip: Click files one at a time. The ZIP option above downloads everything at once.
            </p>
          </Card.Body>
        </Card>
      )}

      {data && (
        <Card className="mb-4">
          <Card.Header>
            <Card.Title>Preview (first 5 rows)</Card.Title>
          </Card.Header>
          <Card.Body>
            <PreviewTable title="Orders_Shopify" rows={data.Orders_By_Channel.Shopify?.slice(0, 5) || []} />
            <PreviewTable title="Ad_Performance" rows={data.Ad_Performance.slice(0, 5)} />
            <PreviewTable title="Orders_Daily_Summary" rows={data.Orders_Daily_Summary.slice(0, 5)} />
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header>
          <Card.Title>How to use in Pliable</Card.Title>
        </Card.Header>
        <Card.Body>
          <ol className="mb-0">
            <li>Click <strong>Generate dataset</strong>.</li>
            <li>Download any individual CSVs below, or grab the ZIP.</li>
            <li>In Pliable, connect a local/file connector and ingest all CSVs.</li>
            <li>Model joins on <strong>OrderID</strong> (orders ↔ items) and <strong>SKU</strong> (items ↔ product master).</li>
            <li>Use <strong>OrderDate/Date</strong> to build Q4 trend visuals and CAC/MER analysis.</li>
          </ol>
        </Card.Body>
      </Card>
    </Container>
  );
}

function PreviewTable({ title, rows }: { title: string; rows: any[] }) {
  if (!rows?.length) return null;
  const cols = Object.keys(rows[0]);
  return (
    <div className="mb-4">
      <h6 className="mb-2">{title}</h6>
      <div className="table-responsive">
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c) => (
                  <td key={c}>{String(r[c])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}